import { useLingui } from "@lingui/react/macro";
import { CSSProperties, useEffect, useState } from "react";

import { Sex } from "../api";
import { dateTimeOrNull, getAge } from "../utils/utils";

/**
 * A patient record reduced to the two props the badge takes. `other` and a
 * missing sex both fall to "" — the badge reads the letter to pick its colour,
 * so anything it can't colour must not reach it as a letter.
 */
export function ageSexOf(patient: {
  sex?: Sex | null;
  date_of_birth?: string | null;
}): { age: number | null; sexLetter: string } {
  return {
    age: patient.date_of_birth ? getAge(patient.date_of_birth) : null,
    sexLetter:
      patient.sex === Sex.M || patient.sex === Sex.F ? patient.sex : "",
  };
}

/**
 * A patient's age and sex as a colour-coded pill — the compact identity tag
 * shown next to a patient's name (profile header, Daily Monitoring's Trends
 * panel). Given a `dateOfBirth` it becomes a button that reveals the date on
 * hover and copies it on click; without one it is a static pill. Either way it
 * is `data-mask`ed, so screen-sharing masks it with the other identifiers.
 */
export function AgeSexBadge(props: {
  age: number | null;
  sexLetter: string;
  dateOfBirth?: string | null;
}) {
  const { age, sexLetter } = props;
  const { t } = useLingui();
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  if (age == null && !sexLetter) return null;
  const text =
    age != null && sexLetter
      ? `${age}${sexLetter}`
      : age != null
        ? `${age}`
        : sexLetter;
  const classes =
    sexLetter === "M"
      ? "bg-sex-male-soft border border-sex-male-border text-sex-male-foreground"
      : sexLetter === "F"
        ? "bg-sex-female-soft border border-sex-female-border text-sex-female-foreground"
        : "bg-muted border border-border text-muted-foreground";
  const badgeClass = `rounded-full px-2 py-0.5 text-xs font-medium ${classes}`;
  const maskStyle = { "--mask-width": "44px" } as CSSProperties;
  const dateOfBirth = dateTimeOrNull(props.dateOfBirth, false);

  if (!dateOfBirth) {
    return (
      <span data-mask style={maskStyle} className={badgeClass}>
        {text}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-mask
      style={maskStyle}
      aria-label={t`Date of birth ${dateOfBirth}, click to copy`}
      className={`cursor-pointer ${badgeClass}`}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={() => setRevealed(false)}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(dateOfBirth);
          setCopied(true);
        } catch (error) {
          // Clipboard can reject (denied permission, insecure context). Don't
          // flash the confirmation, and swallow the rejection so it doesn't
          // surface as an unhandled promise rejection.
          console.warn("Copy to clipboard failed", error);
        }
      }}
    >
      {revealed ? (copied ? t`Copied!` : dateOfBirth) : text}
    </button>
  );
}
